<template>
  <div>
    <main class="flex flex-column">
      <top-nav :isWhite="false" :hasLogo="true" :lang="$route.params.lang"></top-nav>
      <article class="flex flex-wrap justify-between" v-if="content">
        <header
          v-if="content.cover"
          class="px3 py4 col-12 bg-light-grey flex flex-column items-stretch"
          :class="{'white': content.cover}"
          :style="'background-image: linear-gradient(to bottom, rgba(0,0,0,0.3), rgba(0,0,0,0.2)), url(' + getContentAPIBase() + content.cover.url + '); background-size: cover;'"
        >
          <div
            class="col-9 flex flex-auto flex-column items-stretch"
            :class="{'white': content.cover, 'black': !content.cover}"
          >
            <h1 class="m0" v-html="content.title"></h1>
            <div class="flex flex-column items-stretch flex-auto justify-between">
              <p class="m0 mt3" v-html="formatDate(content.createdAt)"></p>
              <social-sharing
                :url="windowLocation"
                :title="content.title"
                :description="convertHTMLToText(content.lead)"
                hashtags="stop_e40"
                inline-template
              >
                <div class="mt1">
                  <network network="twitter">
                    <i class="fa fa-fw fa-twitter social-btn"></i>
                  </network>
                  <network network="facebook">
                    <i class="fa fa-fw fa-facebook social-btn"></i>
                  </network>
                  <network network="vk">
                    <i class="fa fa-fw fa-vk social-btn" aria-hidden="true"></i>
                  </network>
                  <network network="linkedin">
                    <i class="fa fa-fw fa-linkedin social-btn"></i>
                  </network>
                </div>
              </social-sharing>
            </div>
          </div>
        </header>

        <aside class="lg-col-2 md-col-3 sm-col-12 xs-col-12 mt4 pl3">
          <nav id="toc" class="sticky" v-if="articleHeaders.length > 1">
            <ul class="list-reset m0">
              <li class="mb2" v-for="(item, index) in articleHeaders" :key="index">
                <a
                  :href="item.href"
                  class="dark-grey"
                  :class="{'active': false}"
                  v-html="item.title"
                  @click="scrollTo($event, item.id)"
                ></a>
              </li>
            </ul>
          </nav>
        </aside>

        <div class="lg-col-10 md-col-9 sm-col-12 xs-col-12 mb4">
          <p class="px3 mt4 h3 medium" v-if="content.lede" v-html="content.lede"></p>
          <section
            id="article-body"
            class="mt4 px3 lg-col-9 md-col-12 sm-col-12 xs-col-12"
            v-html="content.body"
          ></section>
        </div>
      </article>
    </main>
    <hr width="150" />
    <articles-list :lang="$route.params.lang"></articles-list>
    <footer-bar :lang="$route.params.lang"></footer-bar>
  </div>
</template>

<script>
import HeroUnit from '@/components/HeroUnit'
import ActionNav from '@/components/ActionNav'

export default {
  created () {
    this.init()
    this.scrollTo(null, 'app')
  },

  watch: {
    '$route' (to, from) {
      if (from.params.lang !== to.params.lang || from.params.slug !== to.params.slug) {
        this.init()
        this.scrollTo(null, 'app')
      }
    }
  },

  components: {
    'hero-unit': HeroUnit,
    'action-nav': ActionNav
  },

  data () {
    return {
      content: {},
      articleHeaders: [],
      windowLocation: String(window.location)
    }
  },

  methods: {
    init: function () {
      this.lang = this.lang || this.$route.params.lang || this.defineDefaultLang()
      this.windowLocation = String(window.location)
      this.contentAPI = this.getContentAPIBase()

      let oid = this.$route.params.oid
      if (oid) {
        // load from a menu click
        let url = this.contentAPI + '/contents/' + oid
        fetch(url).then((response) => {
          return response.json()
        }).then((data) => {
          this.content = data
          if (data.lede && data.lede.length > 0) {
            this.content.lede = window.marked.parse(data.lede)
          }
          this.content.body = window.marked.parse(data.body)
        }).then(() => {
          this.articleHeaders = this.getArticleHeaders()
          this.updateMeta()
        }).catch((err) => {
          console.error('fetch error', err)
        })
      } else {
        // load from direct request
        let url = this.contentAPI + '/contents?public=true&language.code=' + this.lang + '&slug=' + this.$route.params.slug
        console.log('fetch', url)
        fetch(url).then((response) => {
          // NOTE: there must be at least one result
          return response.json()
        }).then((data) => {
          this.content = data[0]
          if (this.content && this.content.lede && this.content.lede.length > 0) {
            this.content.lede = window.marked.parse(this.content.lede)
          }
          if (this.content) {
            this.content.body = window.marked.parse(this.content.body)
          }
        }).then(() => {
          this.articleHeaders = this.getArticleHeaders()
          this.updateMeta()
        }).catch((err) => {
          console.error('fetch error', err)
        })
      }
    },

    updateMeta: function () {
      document.title = this.content.title || this.defaultPageTitle[this.lang]

      if (this.content.lede && this.content.lede.length > 0) {
        let descr = this.convertHTMLToText(this.content.lede)
        let m = document.querySelector('meta[name=description]')
        if (m) {
          m.content = descr
        } else {
          let m = document.createElement('meta')
          m.name = 'description'
          m.content = descr
          document.querySelector('head').appendChild(m)
        }
      }

      let oglist = [
        {name: 'og:title', content: this.content.title},
        {name: 'og:type', content: 'article'},
        {name: 'og:image', content: this.getContentAPIBase() + this.content.cover.url},
        {name: 'og:url', content: window.location}
      ]
      for (let og of oglist) {
        let m = document.createElement('meta')
        m.name = og.name
        m.content = og.content
        document.querySelector('head').appendChild(m)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import "../assets/styles/colors";

header {
  min-height: 380px;
}

.sticky {
  position: sticky;
  top: 2em;
}

#toc {
  font-size: 0.8em;
}

td.caps {
  width: 200px;
}

.quote {
  font-family: "Agipo";
  font-size: 2.45em;
  line-height: 1.25em;
  padding-top: 1.25em;
  padding-bottom: 1.25em;
  padding-right: 1em;
  max-width: 30em;
  color: $dark-grey;
}
</style>



// WEBPACK FOOTER //
// Article.vue?242c32be